/**
 * Theme and language.
 *
 * Two themes, chosen by ROLE rather than by taste (gap D12):
 *
 *   COMMAND — the dark navy executive palette. Right for the Secretariat and
 *   for CE / SE / XEN / SDO at a desk.
 *
 *   FIELD — high-contrast light, 48px tap targets. A Sub-Engineer on a canal
 *   bank in direct Punjab sunlight, on a cheap Android phone, cannot read dark
 *   navy. The people who generate every record in this system work outdoors, so
 *   this is a first-class theme and not a "mobile version".
 *
 * Narrow viewports also get Field regardless of role, because a small screen is
 * usually a phone and a phone is usually outdoors. The user can always override,
 * and their choice is remembered per browser.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FIELD_ROLES, type Role } from '@etawan/shared';
import { applyLanguageToDocument, SUPPORTED_LANGUAGES, type Language } from '../i18n';

export type Theme = 'command' | 'field';

const THEME_KEY = 'etawan.theme';
const LANG_KEY = 'etawan.lang';
const FIELD_BREAKPOINT = 768;

/** localStorage throws in private mode and when site data is blocked. */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* A remembered preference is a convenience; losing it must never break the app. */
  }
}

export function defaultThemeFor(role: Role | null, viewportWidth: number): Theme {
  if (viewportWidth < FIELD_BREAKPOINT) return 'field';
  if (role && FIELD_ROLES.includes(role)) return 'field';
  return 'command';
}

interface ThemeContextValue {
  theme: Theme;
  /**
   * Tell the provider which role is signed in, so the role-derived default
   * applies.
   *
   * Passed up rather than down because ThemeProvider sits ABOVE the auth
   * context: the public portal must render without loading Firebase at all, so
   * AuthProvider lives inside the lazily-loaded officer chunk. The officer
   * shell reports its role here once it knows it.
   */
  setRoleHint: (role: Role | null) => void;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  language: Language;
  setLanguage: (l: Language) => void;
  toggleLanguage: () => void;
  /** True when the user has overridden the role-derived default. */
  themeIsOverridden: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [role, setRoleHint] = useState<Role | null>(null);
  const [override, setOverride] = useState<Theme | null>(
    () => (readStored(THEME_KEY) as Theme | null) ?? null,
  );
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const theme = override ?? defaultThemeFor(role, viewportWidth);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  /**
   * Language is React state synced to i18next, not read off `i18n.language`
   * during render.
   *
   * Deriving it at render time looked fine and was wrong: on reload, the
   * remembered language was restored into i18next (so every label rendered in
   * Urdu) while this component's derived value stayed at "en" — which left
   * <html dir> at "ltr" under an Urdu interface, and made the first click of the
   * language toggle appear to do nothing. Reading an external store during
   * render and hoping the re-render lines up is the bug; holding the value in
   * state and subscribing to the store is the fix.
   */
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = readStored(LANG_KEY) as Language | null;
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
    return (
      SUPPORTED_LANGUAGES.includes(i18n.language as Language) ? i18n.language : 'en'
    ) as Language;
  });

  // Subscribe, so a language change from anywhere reaches this state.
  useEffect(() => {
    const onChanged = (lng: string) => {
      if (SUPPORTED_LANGUAGES.includes(lng as Language)) setLanguageState(lng as Language);
    };
    i18n.on('languageChanged', onChanged);
    return () => {
      i18n.off('languageChanged', onChanged);
    };
  }, [i18n]);

  // Push state into i18next and onto <html lang>/<html dir> together, so the
  // document attributes can never lag behind the rendered labels.
  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
    applyLanguageToDocument(language);
  }, [language, i18n]);

  const setTheme = useCallback((t: Theme) => {
    setOverride(t);
    writeStored(THEME_KEY, t);
  }, []);

  const setLanguage = useCallback((l: Language) => {
    writeStored(LANG_KEY, l);
    setLanguageState(l); // the effect above pushes it into i18next and the document
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme: () => setTheme(theme === 'command' ? 'field' : 'command'),
      language,
      setLanguage,
      toggleLanguage: () => setLanguage(language === 'en' ? 'ur' : 'en'),
      themeIsOverridden: override !== null,
      setRoleHint,
    }),
    [theme, setTheme, language, setLanguage, override],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
